import { supabase } from "../supabaseClient"

const unwrap = ({ data, error }, fallback) => {
  if (error) throw error
  return data ?? fallback
}

export async function fetchMonitoringClients(organizationId) {
  return unwrap(await supabase
    .from("accounts")
    .select(`
      id, organization_id, name, monitoring_profile, monitoring_tier,
      monitoring_timezone, monitoring_enabled, monitoring_frequency,
      health_status, baseline_approved_at, next_scan_at, last_successful_scan_at,
      platform_accounts(id, platform, active),
      issues(id, status, severity)
    `)
    .eq("organization_id", organizationId)
    .eq("active", true)
    .order("name"), [])
}

export async function createMonitoringClient(organizationId, values) {
  return unwrap(await supabase.from("accounts").insert({
    organization_id: organizationId,
    name: values.name.trim(),
    monitoring_profile: values.monitoringProfile || "custom_baseline",
    monitoring_tier: "standard",
    monitoring_timezone: values.monitoringTimezone || "America/New_York",
    monitoring_frequency: values.monitoringFrequency || "daily",
    monitoring_enabled: false,
    onboarding_date: new Date().toISOString().slice(0, 10),
  }).select().single())
}

export async function updateMonitoringClient(organizationId, accountId, values) {
  return unwrap(await supabase.from("accounts")
    .update(values)
    .eq("id", accountId)
    .eq("organization_id", organizationId)
    .select()
    .single())
}

export async function fetchMonitoringClient(organizationId, accountId) {
  const [
    account, platforms, conversions, baselines, issues, runs, snapshots, activity,
    rulePacks, websites, ga4Properties, products, paidMediaSetup, measurementMappings,
  ] = await Promise.all([
    supabase.from("accounts").select("*").eq("id", accountId).eq("organization_id", organizationId).single(),
    supabase.from("platform_accounts").select("*").eq("account_id", accountId).eq("organization_id", organizationId).order("platform"),
    supabase.from("business_conversions").select("*, mappings:conversion_platform_mappings(*)").eq("account_id", accountId).eq("organization_id", organizationId).order("name"),
    supabase.from("monitoring_baselines").select("*").eq("account_id", accountId).eq("organization_id", organizationId).order("version", { ascending: false }),
    supabase.from("issues").select("*").eq("account_id", accountId).eq("organization_id", organizationId).order("last_detected_at", { ascending: false }),
    supabase.from("monitoring_runs").select("*").eq("account_id", accountId).eq("organization_id", organizationId).order("created_at", { ascending: false }).limit(25),
    supabase.from("platform_snapshots").select("*").eq("account_id", accountId).eq("organization_id", organizationId).is("valid_to", null),
    supabase.from("conversion_activity_daily").select("*").eq("account_id", accountId).eq("organization_id", organizationId).order("activity_date", { ascending: false }).limit(250),
    supabase.from("account_rule_packs").select("*, rule_pack:monitoring_rule_packs(*)").eq("account_id", accountId).eq("organization_id", organizationId),
    supabase.from("website_urls").select("id, url, active, is_primary").eq("account_id", accountId).eq("organization_id", organizationId).order("url"),
    supabase.from("ga4_properties").select("id, property_id, property_name, template_id").eq("account_id", accountId).eq("organization_id", organizationId).order("property_name"),
    supabase.from("products").select("id, product, event_owner").eq("account_id", accountId).eq("organization_id", organizationId).order("product"),
    supabase.from("account_paid_media_setups").select("id, meta_pixel_connection_id, gtm_container_connection_id, ga4_measurement_id").eq("account_id", accountId).eq("organization_id", organizationId).maybeSingle(),
    supabase.from("domain_measurement_mappings").select("id, normalized_domain, gtm_public_id, ga4_measurement_id, active").eq("account_id", accountId).eq("organization_id", organizationId).eq("active", true).order("normalized_domain"),
  ])
  return {
    account: unwrap(account),
    platforms: unwrap(platforms, []),
    conversions: unwrap(conversions, []),
    baselines: unwrap(baselines, []),
    issues: unwrap(issues, []),
    runs: unwrap(runs, []),
    snapshots: unwrap(snapshots, []),
    activity: unwrap(activity, []),
    rulePacks: unwrap(rulePacks, []),
    websites: unwrap(websites, []),
    ga4Properties: unwrap(ga4Properties, []),
    products: unwrap(products, []),
    paidMediaSetup: unwrap(paidMediaSetup, null),
    measurementMappings: unwrap(measurementMappings, []),
  }
}

export async function fetchOrganizationMonitoringRole(organizationId, userId) {
  return unwrap(await supabase.from("organization_members")
    .select("role")
    .eq("organization_id", organizationId)
    .eq("user_id", userId)
    .maybeSingle(), null)?.role || null
}

export async function enqueueMonitoringRun(accountId) {
  return unwrap(await supabase.rpc("enqueue_monitoring_run", { p_account_id: accountId }))
}

export async function createDraftBaseline({ organizationId, accountId, snapshots }) {
  const { data: latest, error: latestError } = await supabase
    .from("monitoring_baselines")
    .select("version")
    .eq("account_id", accountId)
    .order("version", { ascending: false })
    .limit(1)
    .maybeSingle()
  if (latestError) throw latestError
  const baseline = unwrap(await supabase.from("monitoring_baselines").insert({
    organization_id: organizationId,
    account_id: accountId,
    version: Number(latest?.version || 0) + 1,
    status: "draft",
    notes: "Discovered from the latest connected platform configuration.",
  }).select().single())

  const uniqueItems = new Map()
  for (const snapshot of snapshots || []) {
    for (const observed of snapshot.snapshot?.resources || []) {
      const key = `${snapshot.platform_account_id}:${observed.resourceType}:${observed.canonicalKey}`
      uniqueItems.set(key, {
        organization_id: organizationId,
        baseline_id: baseline.id,
        platform_account_id: snapshot.platform_account_id,
        resource_type: observed.resourceType,
        external_resource_id: observed.externalResourceId || null,
        canonical_key: observed.canonicalKey,
        normalized_hash: observed.normalizedHash,
        expected_state: observed.state || {},
      })
    }
  }
  if (!uniqueItems.size) {
    await supabase.from("monitoring_baselines").delete().eq("id", baseline.id)
    throw new Error("Run configuration discovery before creating a baseline.")
  }
  const { error: itemError } = await supabase
    .from("monitoring_baseline_items")
    .insert([...uniqueItems.values()])
  if (itemError) {
    await supabase.from("monitoring_baselines").delete().eq("id", baseline.id)
    throw itemError
  }
  return baseline
}

export async function approveMonitoringBaseline(baselineId) {
  return unwrap(await supabase.rpc("approve_monitoring_baseline", { p_baseline_id: baselineId }))
}

export async function createBusinessConversion({ organizationId, accountId, observed }) {
  const business = unwrap(await supabase.from("business_conversions").insert({
    organization_id: organizationId,
    account_id: accountId,
    name: observed.name,
    canonical_event_name: observed.eventName || observed.name,
    category: "custom",
    expected_activity: "unknown",
  }).select().single())
  try {
    await unwrap(await supabase.from("conversion_platform_mappings").insert({
      organization_id: organizationId,
      business_conversion_id: business.id,
      platform_account_id: observed.platformAccountId,
      external_conversion_id: observed.externalId || null,
      external_conversion_name: observed.name,
      expected_status: observed.state?.status || null,
      expected_primary_state: observed.state?.primaryForGoal ?? observed.state?.primary_for_goal ?? null,
      expected_event_name: observed.eventName || null,
      metadata: { resource_type: observed.resourceType },
    }))
  } catch (error) {
    await supabase.from("business_conversions").delete().eq("id", business.id)
    throw error
  }
  return business
}

export async function transitionMonitoringIssue(issueId, status, resolution = null) {
  return unwrap(await supabase.rpc("transition_issue", {
    p_issue_id: issueId,
    p_status: status,
    p_resolution: resolution,
  }))
}
